import { type FC, useState } from 'react'
import {
  useGetEnrollmentMultiAccountTransferData,
  useSubmitEnrollmentMultiAccountTransferData,
  useUploadAcatDocument,
  useSubmitAcatDocuments,
  useGetEnrollmentMultiAccountTransferAccountData,
} from '@egw/api'
import { Button, ButtonVariant, GenericError, Tab, TabList, TabPanel, Tabs, Alert, AlertType } from '@egw/components'
import { CUSTOMER_SERVICE_PHONE_NUMBER, CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY, WEALTH_ROUTES } from '@egw/constants'
import { Trans, useTranslation } from '@egw/i18n'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useNavigate } from 'react-router'
import { configClient } from '@egw/config'
import { FileUpload, type UploadedFile } from '@egw/features-system'
import { PageHeader } from '../../../components'
import { type Config } from '../../../config/config'
import { ALL_ENROLLMENT_ROUTES } from '../../../constants'

const log = new Logger('Enrollment - Multi - AccountTransfer')

enum ACCOUNT_TRANSFER_TABS {
  FULL_TRANSFER = '1',
  PARTIAL_TRANSFER = '2',
}

export const AccountTransferPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [isCommonError, setIsCommonError] = useState<boolean>(false)

  const { data: wealthOverviewData, isLoading: isWealthOverviewLoading } =
    useGetEnrollmentMultiAccountTransferAccountData()
  const { isLoading, error } = useGetEnrollmentMultiAccountTransferData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiAccountTransferData()
  const { mutate: uploadDocument } = useUploadAcatDocument()
  const {
    mutate: submitAcatDocuments,
    isLoading: isSubmittingAcatDocuments,
    error: submitAcatDocumentsError,
  } = useSubmitAcatDocuments()

  const config = configClient.getConfig() as Config

  // Protect route
  // Disabled to allow for FE navigation - BE can't navigate these child routes right now
  // useValidateRoute(data?.path)

  const fundAnotherWayHandler = (): void => {
    void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.FUNDING.path}`)
  }

  // TODO: Add a check to see if the user has uploaded the required documents AND pass docs to BE
  const submitTransferHandler = async (): Promise<void> => {
    setIsCommonError(false)

    const companionAccount = wealthOverviewData?.personalPortfolio.accounts
      .filter((e) => e.alias)
      .find((acc) => acc.attributes.acatAllowed === true)

    if (companionAccount?.alias !== undefined) {
      try {
        const submitRes = await submitAcatDocuments({
          accountAlias: companionAccount.alias,
          filePaths: uploadedFiles.map((file) => file.filePath),
          disclosures: [{ id: config.acat.disclosureId, accepted: true }],
        })
        if (submitRes?.__typename === 'SubmitAcatDocumentsSuccess' && submitRes.isSuccessful) {
          await mutate({
            proceedAccountTransfer: true,
          })

          void navigate(
            `${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.ACCOUNT_TRANSFER_SUCCESS.path}`,
          )
        } else {
          setIsCommonError(true)
        }
      } catch (e) {
        log.error('Error submitting form', e)
        setIsCommonError(true)
      }
    } else {
      setIsCommonError(true)
    }
  }

  return (
    <EnrollmentMultiLayout title={t('multi.account_transfer.page_title')}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <>
          <PageHeader title={t('multi.account_transfer.title')} className="!mb-2xl" />
          <h3 className="typo-primary-lg-regular text-text-header mb-sm">{t('multi.account_transfer.subtitle')}</h3>
          <p className="typo-primary-base text-text-meta mb-2xl">{t('multi.account_transfer.description')}</p>
          <Tabs defaultSelectedTab={ACCOUNT_TRANSFER_TABS.FULL_TRANSFER}>
            <TabList>
              <Tab
                id={ACCOUNT_TRANSFER_TABS.FULL_TRANSFER}
                testId="full-transfer-tab"
                label={t('multi.account_transfer.full_transfer')}
              />
              <Tab
                id={ACCOUNT_TRANSFER_TABS.PARTIAL_TRANSFER}
                testId="partial-transfer-tab"
                label={t('multi.account_transfer.partial_transfer')}
              />
            </TabList>
            <TabPanel id={ACCOUNT_TRANSFER_TABS.FULL_TRANSFER} testId="full-transfer-panel" className="px-0 pt-lg">
              <div className="flex flex-col gap-2xl">
                <div className="mt-lg">
                  <FileUpload
                    onChange={(files) => {
                      setUploadedFiles(files)
                    }}
                    mutate={uploadDocument}
                    testId="enrollment-account-transfer-file-upload"
                    listTitle={t('multi.account_transfer.upload_your_statements')}
                    buttonLabel={t('multi.account_transfer.upload_statement')}
                    isDisabled={isSubmittingAcatDocuments || isSubmitting}
                  />
                </div>
                <p className="typo-primary-base text-text-meta">
                  {t('multi.account_transfer.helper_text')}
                  &nbsp;
                  <a
                    href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                    className="typo-primary-base-semibold text-text-action cursor-pointer"
                  >
                    {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                  </a>
                  .
                </p>
                <div className="typo-primary-xs text-text-meta">
                  <Trans
                    t={t}
                    className="typo-primary-xs text-text-meta"
                    i18nKey="multi.account_transfer.submit_transfer_terms"
                  >
                    <a
                      target="_blank"
                      href={config.zendeskArticles.acatTermsAndConditions}
                      className="typo-primary-xs-semibold inline cursor-pointer hover:underline"
                      rel="noopener"
                    >
                      {t('multi.account_transfer.acat_terms_and_conditions')}
                    </a>
                  </Trans>
                </div>

                {(submitError ?? submitAcatDocumentsError ?? isCommonError) ? (
                  <div className="mb-xs">
                    <Alert description={t('multi.account_transfer.error')} alertType={AlertType.Error} />
                  </div>
                ) : null}

                <div className="flex flex-col md:flex-row gap-sm ">
                  <Button
                    testId="full-transfer-fund-another-way-button"
                    variant={ButtonVariant.Secondary}
                    className="w-full md:w-fit"
                    onClick={fundAnotherWayHandler}
                  >
                    {t('multi.account_transfer.fund_another_way')}
                  </Button>
                  <Button
                    testId="full-transfer-submit-transfer-button"
                    variant={ButtonVariant.Primary}
                    className="w-full md:w-fit"
                    isLoading={isSubmitting || isSubmittingAcatDocuments}
                    onClick={() => {
                      void submitTransferHandler()
                    }}
                    isDisabled={uploadedFiles.length === 0 || isWealthOverviewLoading}
                  >
                    {t('multi.account_transfer.submit_transfer_request')}
                  </Button>
                </div>
              </div>
            </TabPanel>
            <TabPanel
              id={ACCOUNT_TRANSFER_TABS.PARTIAL_TRANSFER}
              testId="partial-transfer-panel"
              className="px-0 pt-lg"
            >
              <div className="flex flex-col gap-2xl">
                <p className="typo-primary-base-semibold text-text-body">
                  {t('multi.account_transfer.partial_transfer_description')}
                </p>
                <p className="typo-primary-base text-text-meta">
                  {t('multi.account_transfer.helper_text')}
                  &nbsp;
                  <a
                    href={`tel:${CUSTOMER_SERVICE_PHONE_NUMBER}`}
                    className="typo-primary-base-semibold text-text-action cursor-pointer"
                  >
                    {CUSTOMER_SERVICE_PHONE_NUMBER_DISPLAY}
                  </a>
                  .
                </p>
                <Button
                  testId="partial-transfer-fund-another-way-button"
                  variant={ButtonVariant.Secondary}
                  className="w-full md:w-fit"
                  onClick={fundAnotherWayHandler}
                >
                  {t('multi.account_transfer.fund_another_way')}
                </Button>
              </div>
            </TabPanel>
          </Tabs>
        </>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
